import type { IteroOrder, IteroPatient } from '../../../../../services/iteroBrowse';
import { orEmpty, type DetailFormatters } from '../iteroBrowseFormat.ts';
import { knownFacts, type FactTile } from '../pane/knownFacts.ts';

export function patientFacts(patient: IteroPatient, format: DetailFormatters): FactTile[] {
    return knownFacts([
        { label: 'Date of birth', value: orEmpty(format.dateOnly(patient.dateOfBirth)) },
        { label: 'In iTero since', value: orEmpty(format.date(patient.creationDate)) },
        { label: 'Last scan', value: orEmpty(format.date(patient.lastScanDate)) }
    ]);
}

export function scanCountLabel(count: number, hasMore: boolean): string {
    return hasMore ? `${count}+` : String(count);
}

const ELLIPSIS = '…';

// Keeps both ends visible: iTero IDs differ at the start and the end, rarely in the middle.
export function truncateMiddle(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    const kept = Math.max(maxLength - ELLIPSIS.length, 2);
    const head = Math.ceil(kept / 2);
    const tail = kept - head;
    return `${text.slice(0, head)}${ELLIPSIS}${text.slice(text.length - tail)}`;
}

function scanTime(order: IteroOrder): number {
    const time = order.scanningDate ? Date.parse(order.scanningDate) : Number.NaN;
    return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}

// Newest first; orders with no usable scan date sink to the end in their original order.
export function timelineOrders(orders: readonly IteroOrder[]): IteroOrder[] {
    return [...orders].sort((a, b) => {
        const left = scanTime(a);
        const right = scanTime(b);
        if (left === right) return 0;
        return right > left ? 1 : -1;
    });
}

export function timelineMeta(order: IteroOrder, format: DetailFormatters): string {
    return [orEmpty(format.date(order.scanningDate)), orEmpty(order.doctorName)]
        .filter((part): part is string => part !== null)
        .join(' · ');
}
